import { Asset, AssetDomainNameManagerStatus, AssetManager, AssetMikrotikManagerStatus, AssetMikrotikPingTargetStatus, AssetMikrotikStatus, AssetRemoteDesktopStatus } from "models/Asset";
import prettyBytes from "pretty-bytes";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Spinner } from "reactstrap";
import { getManagedAssetStatus } from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

let NoVNC: any;

const formatBitsPerSecond = (value: number) => {
    const units = ["bps", "kbps", "Mbps", "Gbps", "Tbps"];
    let scaled = Number.isFinite(value) ? Math.max(0, value) : 0;
    let unit = 0;
    while (scaled >= 1000 && unit < units.length - 1) {
        scaled /= 1000;
        unit++;
    }
    const formatted = unit === 0 ? Math.round(scaled).toString() : scaled.toFixed(1).replace(/\.0$/, "");
    return `${formatted} ${units[unit]}`;
};

interface Props {
    asset: Asset;
    manager: AssetManager;
}

const StatusBadge: React.FC<{ connected: boolean; loading: boolean; connectedLabel?: string; state?: AssetMikrotikStatus["state"] }> = ({ connected, loading, connectedLabel, state }) => {
    const { t } = useOfficeTranslation();
    if (loading || state === "connecting") return <span className="badge bg-light text-muted"><Spinner size="sm" className="me-1" />{t("assets.management.connecting")}</span>;
    if (state === "unreachable") return <span className="badge bg-warning bg-soft text-warning"><i className="mdi mdi-alert-circle-outline me-1" />{t("assets.management.unreachable")}</span>;
    if (state === "authentication_failed") return <span className="badge bg-danger bg-soft text-danger"><i className="mdi mdi-lock-alert-outline me-1" />{t("assets.management.authenticationFailed")}</span>;
    if (state === "configuration_error") return <span className="badge bg-danger bg-soft text-danger"><i className="mdi mdi-cog-alert-outline me-1" />{t("assets.management.configurationError")}</span>;
    return <span className={`badge bg-${connected ? "success" : "danger"} bg-soft text-${connected ? "success" : "danger"}`}>
        <i className={`mdi mdi-${connected ? "check-circle-outline" : "close-circle-outline"} me-1`} />
        {connected ? connectedLabel || t("accounts.accountAssetsCard.connected") : t("accounts.accountAssetsCard.offline")}
    </span>;
};

const PingTargetBadge: React.FC<{target: AssetMikrotikPingTargetStatus}> = ({target}) => {
    const {t} = useOfficeTranslation();
    const colors: Record<AssetMikrotikPingTargetStatus["state"], string> = {
        unknown: "secondary", up: "success", degraded: "warning", unreachable: "warning", down: "danger",
    };
    const color = target.stale ? "secondary" : colors[target.state];
    return <span className={`badge bg-${color} bg-soft text-${color}`}>
        <i className={`mdi mdi-${target.stale ? "clock-alert-outline" : target.state === "up" ? "check-circle-outline" : target.state === "down" ? "close-circle-outline" : "alert-circle-outline"} me-1`} />
        {target.stale ? t("assets.management.pingStateStale") : t(`assets.management.pingState.${target.state}`)}
    </span>;
};

const ComputerRow: React.FC<Props> = ({ asset, manager }) => {
    const { t } = useOfficeTranslation();
    const [computer, setComputer] = useState<AssetRemoteDesktopStatus | null>(null);
    const [routerStatus, setRouterStatus] = useState<AssetMikrotikManagerStatus | null>(null);
    const [domainStatus, setDomainStatus] = useState<AssetDomainNameManagerStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [remoteActive, setRemoteActive] = useState(false);

    useEffect(() => {
        let mounted = true;
        let initialRequest = true;

        const refresh = () => {
            getManagedAssetStatus(asset.id, manager.id)
                .then(result => {
                    if (!mounted) return;
                    setLoadFailed(false);
                    if ((result as AssetDomainNameManagerStatus).domain !== undefined) {
                        setDomainStatus(result as AssetDomainNameManagerStatus);
                    } else if ((result as AssetMikrotikManagerStatus).router !== undefined) {
                        setRouterStatus(result as AssetMikrotikManagerStatus);
                    } else {
                        setComputer(result as AssetRemoteDesktopStatus);
                    }
                })
                .catch(() => {
                    if (mounted) setLoadFailed(true);
                })
                .finally(() => {
                    if (mounted && initialRequest) {
                        initialRequest = false;
                        setLoading(false);
                    }
                });
        };

        setComputer(null);
        setRouterStatus(null);
        setDomainStatus(null);
        setLoading(true);
        setLoadFailed(false);
        refresh();

        const interval = manager.type === "mikrotik"
            ? window.setInterval(refresh, 5000)
            : undefined;
        return () => {
            mounted = false;
            if (interval !== undefined) window.clearInterval(interval);
        };
    }, [asset.id, manager.id, manager.type]);

    useEffect(() => {
        if (!remoteActive) return;
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setRemoteActive(false);
        };
        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [remoteActive]);

    const openRemote = async () => {
        if (computer?.connection_state !== "connected") return;
        // @ts-ignore The package does not provide TypeScript declarations.
        NoVNC = (await import("react-novnc")).default;
        setRemoteActive(true);
    };

    const router = routerStatus?.router;
    const isMikrotik = manager.type === "mikrotik";
    const isRemoteDesktop = manager.type === "remote_desktop";
    const isDomainName = manager.type === "domain_name";
    const connected = isMikrotik
        ? router?.state ? router.state === "online" : router?.connected ?? false
        : isDomainName ? domainStatus?.domain.connected ?? false : computer?.connection_state === "connected";
    const detail = isMikrotik
        ? router?.identity || manager.mikrotik?.address
        : isDomainName ? domainStatus?.domain.domain_name || manager.domain_name?.domain_name : computer?.hostname;
    const webUrl = manager.mikrotik?.web_url
        || `${manager.mikrotik?.use_tls ? "https" : "http"}://${manager.mikrotik?.address}`;
    const totalMemory = router?.total_memory ?? 0;
    const usedMemory = Math.max(0, totalMemory - (router?.free_memory ?? 0));
    const interfaces = router?.interfaces ?? [];
    const pingTargets = router?.ping_targets ?? [];
    const pingIssues = pingTargets.filter(target => target.stale || target.state === "degraded" || target.state === "unreachable" || target.state === "down").length;

    return <>
        <div
            className={`d-flex align-items-start gap-3 border-bottom py-3${connected && isRemoteDesktop ? " cursor-pointer" : ""}`}
            onClick={isRemoteDesktop ? openRemote : undefined}
        >
            <div className="avatar-sm flex-shrink-0">
                <span className="avatar-title rounded bg-primary bg-soft text-primary font-size-20">
                    <i className={`mdi ${isMikrotik ? "mdi-router-wireless" : isDomainName ? "mdi-web" : "mdi-remote-desktop"}`} />
                </span>
            </div>
            <div className="min-w-0 flex-grow-1">
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <Link className="fw-semibold text-break" to={`/assets/${asset.id}`} onClick={event => event.stopPropagation()}>{asset.name}</Link>
                    <span className="text-muted small">{manager.name || t(`assets.management.${isMikrotik ? "mikrotik" : isDomainName ? "domainName" : "remoteDesktop"}`)}</span>
                    <StatusBadge connected={connected} loading={loading} connectedLabel={isDomainName ? t("assets.management.checked") : undefined} state={isMikrotik ? router?.state : undefined} />
                    {router?.update_available && <span
                        className="badge bg-warning bg-soft text-warning"
                        title={t("customer.computerRow.latestVersionVersion", { version: router.latest_version })}
                    >
                        <i className="mdi mdi-update me-1" />{t("customer.computerRow.routerOs")} {router.version}
                    </span>}
                    {(computer?.updates_status?.updates_available ?? 0) > 0 && <span className="badge bg-warning bg-soft text-warning">
                        <i className="mdi mdi-update me-1" />{computer?.updates_status?.updates_available}
                    </span>}
                    {isMikrotik && pingIssues > 0 && <span className="badge bg-warning bg-soft text-warning"><i className="mdi mdi-access-point-network-off me-1" />{t(pingIssues === 1 ? "assets.management.pingIssues_one" : "assets.management.pingIssues_other", {count: pingIssues})}</span>}
                </div>
                {detail && <div className="text-muted small text-break">{detail}</div>}
                {isMikrotik && connected && <div className="small mt-2">
                    {router?.version && !router.update_available && <span className="me-2">{t("customer.computerRow.routerOs")} {router.version}</span>}
                    <span>{t("common.cpu")} {router?.cpu_load ?? 0}%</span>
                    {totalMemory > 0 && <span> · {prettyBytes(usedMemory)} / {prettyBytes(totalMemory)}</span>}
                    {interfaces.length > 0 && <div className="text-muted mt-1">
                        {interfaces.map(iface => <div key={iface.name}>
                            {iface.label || iface.name}: ↓ {formatBitsPerSecond(iface.rx_bps ?? 0)}
                            {" · "}↑ {formatBitsPerSecond(iface.tx_bps ?? 0)}
                        </div>)}
                    </div>}
                    {!interfaces.length && router?.interface && <div className="text-muted mt-1">
                        {router.interface}: ↓ {formatBitsPerSecond(router.rx_bps ?? 0)}
                        {" · "}↑ {formatBitsPerSecond(router.tx_bps ?? 0)}
                    </div>}
                    {(router?.board_name || router?.current_firmware) && <div className="text-muted mt-1">
                        {router?.board_name}
                        {router?.current_firmware && <> · {t("customer.computerRow.firmware")} {router.current_firmware}</>}
                    </div>}
                </div>}
                {!loading && !isDomainName && (loadFailed || (isMikrotik && !connected && router?.state !== "connecting")) && <div className="small text-danger mt-1">
                    {router?.error || t("accounts.accountAssetsCard.statusUnavailable")}
                </div>}
                {isMikrotik && !connected && router?.last_success_at && <div className="small text-muted mt-1">
                    {t("assets.management.lastSeen")}: {new Date(router.last_success_at).toLocaleString()}
                </div>}
                {isMikrotik && pingTargets.length > 0 && <div className="mt-2 border-top pt-2">
                    <div className="small fw-semibold mb-1">{t("assets.management.pingTargets")}</div>
                    {pingTargets.map(target => <div className="d-flex justify-content-between align-items-start gap-2 py-1" key={target.target_id}>
                        <div className="small min-w-0">
                            <div className="d-flex align-items-center flex-wrap gap-1">
                                {target.asset_id
                                    ? <Link to={`/assets/${target.asset_id}`} className="fw-semibold text-break" onClick={event => event.stopPropagation()}>{target.name}</Link>
                                    : <span className="fw-semibold text-break">{target.name}</span>}
                                <span className="text-muted text-break">{target.address}</span>
                            </div>
                            {(target.sent > 0 || target.last_success_at) && <div className="text-muted">
                                {target.received > 0 && <>{t("assets.management.averageRtt")}: {target.avg_rtt_ms.toFixed(target.avg_rtt_ms < 10 ? 1 : 0)} {t("common.units.millisecondsShort")} · </>}
                                {target.sent > 0 && <>{t("assets.management.packetLoss")}: {Math.round(target.packet_loss_percent)}%</>}
                                {target.last_success_at && <> · {t("assets.management.lastSeen")}: {new Date(target.last_success_at).toLocaleString()}</>}
                            </div>}
                            {target.error && <div className="text-danger text-break">{target.error}</div>}
                        </div>
                        <PingTargetBadge target={target} />
                    </div>)}
                </div>}
                {isDomainName && domainStatus?.domain.expires_at && <div className="small mt-2">
                    {t("assets.management.expiresAt")}: {new Date(domainStatus.domain.expires_at).toLocaleDateString()}
                    {domainStatus.domain.days_until_expiration !== undefined && <> · {t("assets.management.daysRemaining", {count: domainStatus.domain.days_until_expiration})}</>}
                    {domainStatus.domain.status?.length > 0 && <div className="text-muted">{domainStatus.domain.status.join(", ")}</div>}
                </div>}
                {!loading && isDomainName && !connected && <div className="small text-danger mt-1">{domainStatus?.domain.error || t("accounts.accountAssetsCard.statusUnavailable")}</div>}
                {isRemoteDesktop && computer?.os && <div className="small mt-2">{computer.os}</div>}
            </div>
            <div className="d-flex gap-1 flex-shrink-0">
                {(isMikrotik || isDomainName) && <Button
                    color="light"
                    size="sm"
                    type="button"
                    aria-label={t("accounts.accountAssetsCard.openAsset", { name: asset.name })}
                    onClick={event => {
                        event.stopPropagation();
                        window.open(isDomainName ? `https://${manager.domain_name?.domain_name}` : webUrl, "_blank", "noopener,noreferrer");
                    }}
                >
                    <i className="mdi mdi-open-in-new" />
                </Button>}
                <Button color="light" size="sm" type="button" tag={Link} to={`/assets/${asset.id}/edit`} aria-label={t("accounts.accountAssetsCard.editAsset", { name: asset.name })} onClick={event => event.stopPropagation()}>
                    <i className="mdi mdi-pencil-outline" />
                </Button>
            </div>
        </div>
        {remoteActive && NoVNC && <NoVNC
            connectionName={`api.office.galactic-group.nl/v1/remote-vnc/${computer?.id}/vnc?${computer?.vnc_token}`}
            onDisconnected={() => setRemoteActive(false)}
            isSecure
        />}
    </>;
};

export default ComputerRow;
